<template>
	<BasePopover :id="$options.name" placement="top" :status.sync="status" :target-id="targetId">
		<template #title>
			<div class="title">
				<span>{{ $t("Informations de mon entreprise") }}</span>
				<span class="float-right">{{ `${current_popover_showed}/${total_popovers_to_show}` }}</span>
			</div>
		</template>

		<template #default>
			<b-form-group class="label" :label="$t('Nom')" label-cols="3">
				<VueTypeaheadBootstrap
					v-model.lazy="name"
					v-debounce:300ms="searchByName"
					:show-all-results="true"
					input-class="typeahead_name input grey-border w-100"
					:data="searchByNameResults"
					:min-matching-chars="3"
					:max-matches="3"
					:serializer="input => input.name"
					@hit="selectCompany"
				>
					<template slot="suggestion" slot-scope="{ data }"> {{ data.name }} - {{ data.siren }} </template>

					<template slot="append">
						<b-spinner v-if="searchingByName" small :label="$t('Recherche en cours')" />
					</template>
				</VueTypeaheadBootstrap>
			</b-form-group>

			<b-form-group class="label" :label="$t('SIREN national id')" label-cols="3">
				<VueTypeaheadBootstrap
					v-model.lazy="siren"
					v-debounce:300ms="searchBySiren"
					input-class="typeahead_siren input grey-border w-100"
					:data="searchBySirenResults"
					:min-matching-chars="9"
					:max-matches="3"
					:serializer="input => input.siren"
					@hit="selectCompany"
				>
					<template slot="suggestion" slot-scope="{ data }"> {{ data.siren }} - {{ data.name }} </template>

					<template slot="append">
						<b-spinner v-if="searchingBySiren" small label="Recherche en cours" />
					</template>
				</VueTypeaheadBootstrap>
			</b-form-group>

			<div v-if="siren && siren.length === 9 && name">
				<b-form-group class="label" :label="$t('Statut')" label-cols="3">
					<b-form-select v-model="legalForm" size="sm" class="input grey-border w-100">
						<b-form-select-option v-for="({ abbr }, index) in legalForms" :key="index" :value="abbr">
							{{ abbr }}
						</b-form-select-option>
					</b-form-select>
					<b-form-input 
						v-if="display_other_form_input_text"
						v-model="legal_form_db_stored"
						:placeholder="$t('Précisez ou laissez vide')"
					/>
				</b-form-group>

				<b-form-group class="label" :label="$t('Adresse')" label-cols="3">
					<b-form-input v-model="address" class="input grey-border w-100" />
				</b-form-group>

				<b-form-group class="label" :label="$t('Tél.')" label-cols="3">
					<b-form-input v-model="phone" class="input grey-border w-100" />
				</b-form-group>

				<b-form-group class="label" :label="$t('N° TVA')" label-cols="3">
					<b-form-input v-model="vatNumber" class="input grey-border w-100" />
				</b-form-group>

				<b-form-group class="label" :label="$t('C° Naf')" label-cols="3">
					<b-form-input v-model="naf" class="input grey-border w-100" />
				</b-form-group>

				<b-form-group class="label" :label="$t('Capital')" label-cols="3">
					<b-form-input v-model="capital" class="input grey-border w-100" type="number" step="0.01" />
				</b-form-group>
			</div>
			<s-button type="" :disabled="!(siren && siren.length === 9 && name)" @click="onSubmit">
				{{ $t("Suivant") }}
			</s-button>

			<b-link class="link grey-color" @click="next"> {{ $t("Ne pas configurer mon entreprise") }} </b-link>
		</template>
	</BasePopover>
</template>

<script>
	import { mapState } from "vuex";
	import { query } from "sinao-corejs";
	import axios from "axios";
	import { getDirective } from "vue-debounce";
	import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";

	import legalForms from "../../json/legal_forms_sinao.json";
	import legalFormsOpendatasoft from "../../json/legal_forms_opendatasoft.json";

	const OPENDATASOFT_API = process.env.VUE_APP_OPENDATASOFT_API;

	const getSearchUrlForOrganization = (q, rows = 1) => {
		return `${OPENDATASOFT_API}?dataset=economicref-france-sirene-v3%40public&q="${q}"&lang=fr&rows=${rows}&facet=adresseetablissement&facet=complementadresseetablissement&facet=libellecommuneetablissement&facet=codecommuneetablissement&facet=denominationunitelegale&facet=prenomusuelunitelegale&facet=nomunitelegale&facet=naturejuridiqueunitelegale&facet=siren&facet=nic&facet=activiteprincipaleetablissement`;
	};

	const getSearchUrlForIndividual = (q, rows = 1) => {
		return `${OPENDATASOFT_API}?dataset=inpi-representants-des-entreprises%40public&q="${q}"&lang=fr&rows=${rows}&facet=qualite&facet=type&facet=libelle_evt&facet=nom_greffe&facet=date_greffe&facet=forme_juridique`;
	};

	export default {
		name: "PopoverCompany",
		components: {
			VueTypeaheadBootstrap
		},
		directives: {
			debounce: getDirective()
		},
		props: {
			targetId: {
				type: String,
				required: true
			}
		},
		data() {
			return {
				delay: 1000,
				legalForms,
				legalFormsOpendatasoft,
				name: null,
				siren: null,
				nic: null,
				legalForm: null,
				address: null,
				phone: null,
				vatNumber: null,
				naf: null,
				capital: 0,
				searchBySirenResults: [],
				searchByNameResults: [],
				searchingByName: false,
				searchingBySiren: false,
				legal_form_db_stored: '',
				display_other_form_input_text: false
			};
		},
		computed: {
			...mapState({
				appId: state => state.app.id,
        total_popovers_to_show: state => state.onboarding.total_popovers_to_show,
        current_popover_showed: state => state.onboarding.current_popover_showed
			}),
			status: {
				get() {
					return this.$store.state.onboarding.showPopoverCompany;
				},
				set(value) {
					this.$store.commit("showPopoverCompany", value);
				}
			},
			organization: {
				get() {
					return this.$store.state.app.organization;
				},
				set(value) {
					this.$store.commit("organization", value);
				}
			},
			legalFormsFlat() {
				return Object.keys(this.legalForms);
			}
		},
		watch: {
			legalForm(newValue) {
				if (newValue === "Autre") {
					this.legal_form_db_stored = "";
					this.display_other_form_input_text = true
				} else {
					this.legal_form_db_stored = newValue;
					this.display_other_form_input_text = false;
				}
			}
		},
		methods: {
			async onSubmit() {
				const { appId, name, siren, vatNumber, legal_form_db_stored, nic, phone, address, naf, capital } = this;

				const organization = {
					name,
					billing_name: name,
					national_id: siren,
					vat_id: vatNumber ? String(vatNumber) : null,
					legal_form: legal_form_db_stored,
					code_naf: naf ? naf.replace(".", "") : null,
					country_iso2: "FR",
					capital,
					headquarters: {
						nic,
						place: {
							formatted_address: address
						}
					}
				};

				if (phone) {
					organization.headquarters.phone = phone;
				}

				this.organization = organization;

				query({
					operationId: "app.organization.update",
					token: this.token,
					params: { appId },
					data: organization
				});

				this.next();
			},
			async searchBySiren(siren) {
				const rows = 1;

				if (siren.length < 9) {
					this.searchBySirenResults = [];

					return;
				}

				if (!siren) {
					this.searchBySirenResults = [];

					return;
				}

				try {
					this.searchingBySiren = true;

					const response = await axios.get(getSearchUrlForOrganization(siren, rows));

					const filtered1 = response.data?.records?.map(record => record.fields);

					const filtered2 = this.filterObjectsByKey(filtered1, "siren");
					const format = [];

					filtered2.forEach(filter => {
					format.push({
						...filter,
						name:
							filter.denominationusuelleetablissement ||
							filter.nomunitelegale ||
							filter.denominationunitelegale
					});
					});
				
					this.searchBySirenResults = format.length > 0 ? format : [];
				} catch (error) {
					console.error(error);
				} finally {
					this.searchingBySiren = false;
					this.searchByNameResults = [];
				}
			},
			async searchByName(name) {
				const rows = 3;

				if (name.length < 3) {
					this.searchByNameResults = [];

					return;
				}

				if (!name) {
					this.searchByNameResults = [];

					return;
				}

				try {
					this.searchingByName = true;

					const response1 = await axios.get(getSearchUrlForOrganization(name, rows));
					const response2 = await axios.get(getSearchUrlForIndividual(name, rows));

					// eslint-disable-next-line compat/compat
					Promise.all([response1, response2]).then(response => {
						const [response1, response2] = response;

						const fiels1 = response1.data?.records?.map(record => record.fields);
						const fiels2 = response2.data?.records?.map(record => record.fields);
						const concat = fiels1.concat(fiels2);
						const filtered = this.filterObjectsByKey(concat, "siren");
						const format = [];

						filtered.forEach(filter => {
							format.push({
								...filter,
								name:
									filter.denominationunitelegale ||
									filter.prenomusuelunitelegale ||
									filter.nomunitelegale ||
									filter.denomination ||
									filter.nom_prenoms
							});
						});
						
						this.searchByNameResults = format.length > 0 ? format : [];
					});
				} catch (error) {
					console.error(error);
				} finally {
					this.searchingByName = false;
					this.searchBySirenResults = [];
				}
			},
			async getVatNumber(siren) {
				try {
					const { data } = await query({
						operationId: "services.vies.get",
						params: {
							siren
						}
					});

					if (data && data?.vat_id) {
						return data?.vat_id;
					}
				} catch (error) {
					console.error(error);
				}
			},
			async selectCompany(organization) {
				const {
					adresseetablissement,
					complementadresseetablissement,
					libellecommuneetablissement,
					codepostaletablissement,
					denominationunitelegale,
					prenomusuelunitelegale,
					nomunitelegale,
					naturejuridiqueunitelegale,
					nic,
					siren,
					nom_patronymique,
					prenoms,
					rep_perm_nom,
					rep_perm_prenoms,
					activiteprincipaleetablissement
				} = organization;

				// Nic
				this.nic = nic;

				// Siren
				this.siren = siren;
				document.getElementsByClassName("typeahead_siren")[0].value = this.siren;

				// Naf
				this.naf = activiteprincipaleetablissement;

				// Address
				if (adresseetablissement) {
					this.address = adresseetablissement;
				}

				if (complementadresseetablissement) {
					this.address = `${this.address}, ${complementadresseetablissement}`;
				}

				if (libellecommuneetablissement) {
					this.address = `${this.address}, ${libellecommuneetablissement}`;
				}

				if (codepostaletablissement) {
					this.address = `${this.address} ${codepostaletablissement}`;
				}

				// Name
				if (denominationunitelegale) {
					this.name = denominationunitelegale;
				} else if (prenomusuelunitelegale || nomunitelegale) {
					this.name = `${prenomusuelunitelegale} ${nomunitelegale}`;
				} else if (nom_patronymique || prenoms) {
					this.name = `${nom_patronymique} ${prenoms}`;
				} else if (rep_perm_nom || rep_perm_prenoms) {
					this.name = `${rep_perm_nom} ${rep_perm_prenoms}`;
				}

				document.getElementsByClassName("typeahead_name")[0].value = this.name;

				// Legal form
				const convertLegalOpendatasoftToSinao = this.legalFormsOpendatasoft[naturejuridiqueunitelegale];

				this.legalForm = this.legalForms[convertLegalOpendatasoftToSinao]
					? this.legalForms[convertLegalOpendatasoftToSinao].abbr
					: this.legalForms["Autre"].abbr;

				// TVA
				this.vatNumber = await this.getVatNumber(this.siren);
			},
			next() {
				query({
					operationId: "app.settings.update",
					params: {
						appId: this.appId
					},
					data: {
						settings: {
							"installation.onboarding.showPopoverCompany": 1
						}
					}
				});

				this.status = false;
        this.$store.commit("setPopovers", { key: "showPopoverCompany", value: false });
        this.$nextTick(() => {
          this.$store.dispatch('setNextOpenedPopover');
          this.$store.dispatch('incrementCurrentPopoverShowed');
        });
			}
		}
	};
</script>

<style lang="scss">
	.input-group-append {
		position: absolute;
		top: 7px;
		left: 180px;
		z-index: 999;
	}

	.input-group > .custom-select:not(:last-child),
	.input-group > .form-control:not(:last-child) {
		border-radius: 5px;
	}
</style>
